/**
 * Typed entry points for app-wide usage analytics — everything outside the
 * two tools: dashboard features, page views and settings changes. Same model
 * as `drawing-analytics.ts`: one family per question, the specific key in
 * `context`, unions as the single source of the vocabulary.
 *
 * Entity lifecycle events (drawing created, project created, legend CRUD…)
 * are NOT re-tracked here: the legacy numbered EventTypes already cover them,
 * mostly emitted server-side.
 */
import { EventType } from "@/types/event-logs.types";
import type { EventContext } from "@/types/event-logs.types";
import { enqueueEvent } from "./event-queue";

export type AppFeature =
  | "member_invited"
  | "member_role_changed"
  | "search_used"
  | "filters_used"
  | "manual_sync_started"
  | "field_observation_created"
  | "field_part_created"
  | "field_part_template_used"
  | "chatbot_opened"
  | "chatbot_message_sent"
  | "pwa_update_applied"
  // Migration diagnosticrapid.fr → inspexio.com (cf. src/brand/migration.ts).
  | "brand_migration_modal_opened"
  | "brand_migration_redirect_clicked";

export function trackAppFeature(
  feature: AppFeature,
  details?: EventContext,
): void {
  enqueueEvent(EventType.APP_FEATURE_USED, { feature, ...details });
}

/**
 * A top-level page was displayed. Tracked from the router, once per
 * navigation, on the route's name — never the full path, which can carry ids.
 */
export function trackPageView(page: string): void {
  enqueueEvent(EventType.APP_PAGE_VIEWED, { page });
}

/** A user setting was changed. `setting` is the stable key, `value` the new value. */
export function trackAppSetting(
  setting: string,
  value: string | number | boolean,
): void {
  enqueueEvent(EventType.APP_SETTING_CHANGED, { setting, value });
}
